import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import axios from 'axios';
import { 
  Trophy, 
  Calendar, 
  Users, 
  MapPin,
  ArrowRight,
  Loader2,
  TrendingUp,
  TrendingDown,
  Minus
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const LeagueLanding = () => {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchLeagueData();
  }, [slug]);

  const fetchLeagueData = async () => {
    try {
      const response = await axios.get(`${API}/league/${slug}`);
      setData(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'League not found');
    } finally {
      setLoading(false);
    }
  };

  const getStreakIcon = (streak) => {
    if (!streak || streak === '-') return <Minus className="w-4 h-4 text-gray-400" />;
    if (streak.startsWith('W')) return <TrendingUp className="w-4 h-4 text-green-500" />;
    if (streak.startsWith('L')) return <TrendingDown className="w-4 h-4 text-red-500" />;
    return <Minus className="w-4 h-4 text-gray-400" />;
  };

  const getStreakColor = (streak) => {
    if (!streak || streak === '-') return 'text-gray-400';
    if (streak.startsWith('W')) return 'text-green-500 font-bold';
    if (streak.startsWith('L')) return 'text-red-500 font-bold';
    return 'text-gray-400';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen">
        <Trophy className="w-16 h-16 text-muted-foreground/50 mb-4" />
        <h1 className="text-2xl font-bold mb-2">League Not Found</h1>
        <p className="text-muted-foreground">{error}</p>
        <Link to="/leagues" className="mt-4">
          <Button>Browse Leagues</Button>
        </Link>
      </div>
    );
  }

  const { league, teams, upcoming_games, standings } = data;
  const primaryColor = league.primary_color || '#ff6b35';

  return (
    <div className="min-h-screen" data-testid="league-landing-page">
      {/* Hero Banner */}
      <div 
        className="relative h-64 md:h-80 bg-cover bg-center"
        style={{ 
          backgroundColor: primaryColor,
          backgroundImage: league.banner_url ? `url(${league.banner_url})` : undefined
        }}
      >
        <div className="absolute inset-0 bg-black/50" />
        <div className="relative container mx-auto px-4 h-full flex items-center">
          <div className="flex items-center gap-6">
            {league.logo_url && (
              <img 
                src={league.logo_url} 
                alt={league.name} 
                className="w-24 h-24 md:w-32 md:h-32 rounded-xl bg-white p-2 shadow-lg"
              />
            )}
            <div className="text-white">
              <Badge className="mb-2 bg-white/20">{league.age_category} • {league.gender}</Badge>
              <h1 className="text-3xl md:text-5xl font-black">{league.name}</h1>
              <p className="text-white/80 mt-2 max-w-xl">{league.description}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Standings */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Trophy className="w-5 h-5" style={{ color: primaryColor }} />
                  Standings
                </CardTitle>
              </CardHeader>
              <CardContent>
                {standings && standings.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b text-sm text-muted-foreground">
                          <th className="text-left py-2 px-2">Rank</th>
                          <th className="text-left py-2 px-2">Team</th>
                          <th className="text-center py-2 px-2">W</th>
                          <th className="text-center py-2 px-2">L</th>
                          <th className="text-center py-2 px-2">PF</th>
                          <th className="text-center py-2 px-2">PA</th>
                          <th className="text-center py-2 px-2">STRK</th>
                        </tr>
                      </thead>
                      <tbody>
                        {standings.map((team, index) => (
                          <tr 
                            key={team.team_id} 
                            className="border-b hover:bg-muted/30"
                            data-testid={`standings-row-${index}`}
                          >
                            <td className="py-3 px-2 font-bold">{team.rank}</td>
                            <td className="py-3 px-2">
                              <div className="flex items-center gap-2">
                                {team.logo_url ? (
                                  <img src={team.logo_url} alt="" className="w-8 h-8 rounded-full" />
                                ) : (
                                  <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center">
                                    <Users className="w-4 h-4" />
                                  </div>
                                )}
                                <span className="font-medium">{team.team_name}</span>
                              </div>
                            </td>
                            <td className="py-3 px-2 text-center font-semibold text-green-600">{team.wins}</td>
                            <td className="py-3 px-2 text-center font-semibold text-red-600">{team.losses}</td>
                            <td className="py-3 px-2 text-center">{team.points_for}</td>
                            <td className="py-3 px-2 text-center">{team.points_against}</td>
                            <td className="py-3 px-2 text-center">
                              <div className="flex items-center justify-center gap-1">
                                {getStreakIcon(team.streak)}
                                <span className={getStreakColor(team.streak)}>{team.streak}</span>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <Trophy className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No games played yet</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Upcoming Games */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="w-5 h-5" style={{ color: primaryColor }} />
                  Upcoming Games
                </CardTitle>
              </CardHeader>
              <CardContent>
                {upcoming_games && upcoming_games.length > 0 ? (
                  <div className="space-y-3">
                    {upcoming_games.map((game, index) => (
                      <div 
                        key={game.id} 
                        className="flex items-center justify-between p-3 rounded-lg border bg-muted/30"
                      >
                        <div className="flex items-center gap-4">
                          <div className="text-center">
                            <p className="text-sm text-muted-foreground">
                              {new Date(game.scheduled_time).toLocaleDateString()}
                            </p>
                            <p className="font-semibold">
                              {new Date(game.scheduled_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </p>
                          </div>
                          <div>
                            <p className="font-medium">{game.team1_name} vs {game.team2_name}</p>
                            <p className="text-sm text-muted-foreground">{game.field || 'Field TBD'}</p>
                          </div>
                        </div>
                        {game.status === 'live' && (
                          <Badge className="bg-red-500 animate-pulse">LIVE</Badge>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <Calendar className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No upcoming games</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* League Info */}
            <Card>
              <CardHeader>
                <CardTitle>League Info</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-2 text-sm">
                  <Calendar className="w-4 h-4 text-muted-foreground" />
                  <span>{new Date(league.start_date).toLocaleDateString()} - {new Date(league.end_date).toLocaleDateString()}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <MapPin className="w-4 h-4 text-muted-foreground" />
                  <span>{league.venue_name || league.location || 'Location TBD'}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Users className="w-4 h-4 text-muted-foreground" />
                  <span>{teams?.length || 0} / {league.max_teams} Teams</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Trophy className="w-4 h-4 text-muted-foreground" />
                  <span>{league.weeks} Week Season</span>
                </div>
              </CardContent>
            </Card>

            {/* Teams */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>Teams</span>
                  <Badge variant="secondary">{teams?.length || 0}</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {teams && teams.length > 0 ? (
                  <div className="space-y-2">
                    {teams.map((team) => (
                      <Link 
                        key={team.id} 
                        to={`/teams/${team.id}`}
                        className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted/50 transition-colors"
                      >
                        {team.logo_url ? (
                          <img src={team.logo_url} alt="" className="w-8 h-8 rounded-full" />
                        ) : (
                          <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center">
                            <Users className="w-4 h-4" />
                          </div>
                        )}
                        <span className="font-medium">{team.name}</span>
                        <ArrowRight className="w-4 h-4 ml-auto text-muted-foreground" />
                      </Link>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-muted-foreground py-4">No teams yet</p>
                )}
              </CardContent>
            </Card>

            {/* Register Button */}
            {league.status === 'upcoming' && (
              <Button className="w-full btn-primary" size="lg">
                Register Your Team
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
